import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation } from '@tanstack/react-query'
import {
  Field as FormikField,
  Form,
  Formik,
  FormikHelpers,
  FormikProps,
} from 'formik'
import { FormikErrors } from 'formik/dist/types'
import React, { useState } from 'react'
import invariant from 'tiny-invariant'
import * as yup from 'yup'

import CompleteRegistrationFormikDirtyNavigationGuard from '@/components/flows/CompleteRegistrationFlow/components/CompleteRegistrationFormikDirtyNavigationGuard'
import SelectFcaServiceAreaField from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/fields/SelectFcaServiceAreaField'
import SelectFcaServiceTypeField from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/components/fields/SelectFcaServiceTypeField'
import { ServiceFormValues } from '@/components/flows/CompleteRegistrationFlow/steps/1_CompanyServices/types'
import CheckboxInput from '@/components/form/CheckboxInput'
import FieldError from '@/components/form/FieldError'
import TextareaInput from '@/components/form/TextareaInput'
import ActionButton from '@/components/misc/ActionButton'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { FieldLabel } from '@/components/ui/field'
import { Heading } from '@/components/ui/heading'
import useCurrentCompanyOrFail from '@/hooks/useCurrentCompanyOrFail'
import useNotyf from '@/hooks/useNotyf'
import { useShowGenericServerError } from '@/hooks/useShowGenericServerError'
import {
  API_PATH_INTERNAL,
  API_VIOLATION_CODE,
  LONG_MESSAGE_DURATION,
} from '@/lib/constants'
import { faPencil } from '@/lib/fontAwesomePro/solid'
import constraintViolationService from '@/lib/services/constraintViolationService'
import errorTypeService from '@/lib/services/errorTypeService'
import fcaServiceService from '@/lib/services/fcaServiceService'
import serviceService from '@/lib/services/serviceService'
import { cn } from '@/lib/utils'
import { CreateOrUpdateFcaServiceRequest } from '@/types/requests/fca-services'
import { CompanyServiceCollectionItem } from '@/types/responses/companies'

interface Props {
  service: ServiceFormValues
  onSave?: (newService: CompanyServiceCollectionItem) => void
  onDelete: () => void
  isEditingByDefault: boolean
}

const ServiceFormFields: Record<
  keyof ServiceFormValues,
  keyof ServiceFormValues
> = {
  id: 'id',
  company: 'company',
  serviceArea: 'serviceArea',
  isOnlyInConjunctionWithFP: 'isOnlyInConjunctionWithFP',
  notes: 'notes',
  serviceType: 'serviceType',
}

const CompanyFcaServiceDetailsRow: React.FC<Props> = ({
  service,
  onSave,
  onDelete,
  isEditingByDefault,
}) => {
  const currentCompany = useCurrentCompanyOrFail()
  const notyf = useNotyf()
  const [isDeleting, setIsDeleting] = useState<boolean>(false)
  const [isEditing, setIsEditing] = useState<boolean>(isEditingByDefault)
  const showGenericServerError = useShowGenericServerError()

  const addOrUpdateServiceMutation = useMutation({
    mutationFn: async ({
      values,
    }: {
      values: ServiceFormValues
      formikHelpers: FormikHelpers<ServiceFormValues>
    }) => {
      if (!values.serviceArea) {
        return
      }

      invariant(values.serviceType, 'Expected serviceType to be defined')

      const request: CreateOrUpdateFcaServiceRequest = {
        company: `${API_PATH_INTERNAL}/companies/${currentCompany.id}`,
        serviceArea: `${API_PATH_INTERNAL}/service-areas/${values.serviceArea.id}`,
        isStandaloneService: !values.isOnlyInConjunctionWithFP,
        serviceType: values.serviceType['@id'],
        notes: values.notes,
      }

      return values.id
        ? fcaServiceService.updateFcaService(values.id, request)
        : fcaServiceService.createFcaService(request)
    },
    onSuccess: (newService) => {
      if (onSave) {
        onSave(newService)
      }

      setIsEditing(false)
    },
    onError: (error, { formikHelpers }) => handleApiError(error, formikHelpers),
  })

  const deleteServiceMutation = useMutation({
    mutationFn: async ({
      serviceId,
    }: {
      serviceId: number
      formikHelpers:
        | FormikHelpers<ServiceFormValues>
        | FormikProps<ServiceFormValues>
    }) => serviceService.deleteService(serviceId),
    onSuccess: onDelete,
    onError: (error, { formikHelpers }) => {
      setIsDeleting(false)

      const cannotDeleteServiceError =
        constraintViolationService.getViolationByCode(
          error,
          API_VIOLATION_CODE.cannotDeleteService,
        )

      if (cannotDeleteServiceError) {
        notyf.open({
          type: 'error',
          message: `Cannot delete this service. ${cannotDeleteServiceError.message}`,
          duration: LONG_MESSAGE_DURATION,
        })

        return
      }

      handleApiError(error, formikHelpers)
    },
  })

  async function handleSubmit(
    values: ServiceFormValues,
    formikHelpers:
      | FormikHelpers<ServiceFormValues>
      | FormikProps<ServiceFormValues>,
  ) {
    await addOrUpdateServiceMutation.mutateAsync({ values, formikHelpers })

    formikHelpers.setSubmitting(false)
  }

  function handleRemove(formikProps: FormikProps<ServiceFormValues>) {
    setIsDeleting(true)

    if (service.id) {
      deleteServiceMutation.mutate({
        serviceId: service.id,
        formikHelpers: formikProps,
      })
    } else
      // No service.id means this service hasn't been saved yet.
      // Delete it in 300ms to allow a nice transition.
      setTimeout(onDelete, 300)
  }

  function handleApiError(
    error: unknown,
    formikHelpers: {
      setErrors: (errors: FormikErrors<ServiceFormValues>) => void
    },
  ) {
    if (errorTypeService.isConstraintViolationListError(error)) {
      const dependantServicesError =
        constraintViolationService.getViolationByCode(
          error,
          API_VIOLATION_CODE.dependantServices,
        )

      if (dependantServicesError) {
        notyf.open({
          type: 'error',
          message: dependantServicesError.message,
          duration: LONG_MESSAGE_DURATION,
        })

        return
      }

      formikHelpers.setErrors(constraintViolationService.formatErrors(error))
      return
    }

    showGenericServerError()
  }

  const validationSchema = yup.object().shape({
    id: yup.number().min(0).nullable(),
    company: yup.string().required('Required'),
    serviceArea: yup.object().shape({
      allowsNonStandaloneServices: yup.bool(),
    }),
    notes: yup.string().nullable(),
    serviceType: yup.object().required('Required'),
  })

  return (
    <div className="text-left">
      {!isEditing && (
        <Card
          className="m-0 flex cursor-pointer flex-row gap-4 border p-4
            text-left"
          onClick={() => setIsEditing(true)}
        >
          <CardContent className="grow px-0">
            <Heading size={2} className="m-0">
              {service.serviceArea?.name}
            </Heading>

            <p className="m-0">
              {service.isOnlyInConjunctionWithFP
                ? 'Offered with financial planning'
                : 'Offered standalone'}
              {service.serviceType === undefined
                ? ''
                : ` | ${service.serviceType.name}`}
              {service.notes ? (
                <>
                  {' '}
                  | <em>{service.notes}</em>
                </>
              ) : (
                ''
              )}
            </p>
          </CardContent>

          {/* Not a button because we're already inside a button that does the same thing,
          this is just in case it's not clear to the user that they can just click the card */}
          <Button
            variant="outline-primary"
            size="lg"
            onClick={() => setIsEditing(true)}
          >
            <FontAwesomeIcon icon={faPencil} />
            Edit
          </Button>
        </Card>
      )}

      {isEditing && (
        <Formik
          initialValues={service}
          enableReinitialize
          validateOnChange
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formikProps) => {
            const { errors, touched, isSubmitting, submitForm } = formikProps

            return (
              <Card>
                <CardContent>
                  <Form
                    className={cn({
                      'pop-in': !service.id,
                    })}
                  >
                    <CompleteRegistrationFormikDirtyNavigationGuard />

                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                      {/* Service */}
                      <div className="flex flex-col gap-2">
                        <div>
                          <FieldLabel className="mb-2">Service</FieldLabel>

                          <FormikField
                            component={SelectFcaServiceAreaField}
                            name={ServiceFormFields.serviceArea}
                          />

                          {errors.serviceArea && touched.serviceArea ? (
                            <FieldError>{errors.serviceArea}</FieldError>
                          ) : null}
                        </div>

                        {/* Is Standalone */}
                        <CheckboxInput
                          label={
                            <div className="text-xs leading-normal">
                              Only offered in conjunction with holistic
                              financial planning?
                            </div>
                          }
                          name={ServiceFormFields.isOnlyInConjunctionWithFP}
                        />
                      </div>

                      {/* Independence status */}
                      <div>
                        <FieldLabel className="mb-2">
                          Independence status
                        </FieldLabel>

                        <FormikField
                          component={SelectFcaServiceTypeField}
                          name={ServiceFormFields.serviceType}
                        />

                        {errors.serviceType && touched.serviceType ? (
                          <FieldError>{errors.serviceType}</FieldError>
                        ) : null}
                      </div>

                      {/* Notes */}
                      <div>
                        <TextareaInput
                          label="Notes"
                          name={ServiceFormFields.notes}
                          placeholder="Notes"
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-end gap-2">
                      {formikProps.dirty && (
                        <Button
                          variant="outline-secondary"
                          onClick={() => setIsEditing(false)}
                          size="sm"
                        >
                          Cancel changes
                        </Button>
                      )}

                      <ActionButton
                        variant="outline-destructive"
                        onClick={() => handleRemove(formikProps)}
                        isProcessing={isDeleting}
                        size="sm"
                      >
                        Remove
                      </ActionButton>

                      <ActionButton
                        isProcessing={isSubmitting}
                        isProcessingText="Saving"
                        showSpinner={isSubmitting}
                        variant="success"
                        size="sm"
                        onClick={() => {
                          if (!isSubmitting) {
                            submitForm()
                          }
                        }}
                      >
                        Save
                      </ActionButton>
                    </div>
                  </Form>
                </CardContent>
              </Card>
            )
          }}
        </Formik>
      )}
    </div>
  )
}

export default CompanyFcaServiceDetailsRow
